import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faUserSlash } from '@fortawesome/pro-light-svg-icons'

import {
  Link, Table as BaseTable, StatusBadge, Text, UserProfileImage,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'

import type { TaskListItemModel } from '@models/types'

import { getColor } from '@functions/status'

import type { EntitiesState } from '@redux/entities'

import { generateUrls, useRelations } from '@hooks/useTaskListItem'

import DateBadge from '@components/DateBadge'

const Title = (props: { taskListItem: TaskListItemModel }) => {
  const { taskListItem } = props

  const { parentTask } = useRelations(taskListItem)
  const { editTaskListItemUrl } = generateUrls(parentTask, { includeRedirect: true })

  return (
    <Link
      href={editTaskListItemUrl}
      textProps={{
        color: 'bodyFontColor', display: 'inline-block', variant: 'ellipsis', width: 320,
      }}
    >
      {taskListItem?.title}
    </Link>
  )
}

const buildColumns = (entities: EntitiesState) => {
  const {
    images, taskListItems, users,
  } = entities

  return [
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        verticalAlign: 'middle',
        width: 320,
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => (
        <Title taskListItem={item} />
      ),
    },
    {
      title: 'Parent Task',
      dataKey: 'parent_task_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}

        return (
          <Text color="bodyFontColor" display="inline-block" width={240} style={{ whiteSpace: 'nowrap' }}>
            {parentTask?.title || 'Not Loaded...'}
          </Text>
        )
      },
    },
    {
      title: 'Due Date',
      cellProps: {
        key: 'due_date',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const dueDate = digObject(parentTask, 'dates.due.date')

        if (!dueDate) return (<Text fontStyle="italic">Not Set</Text>)

        return (
          <DateBadge date={dueDate} />
        )
      },
    },
    {
      title: 'Priority',
      cellProps: {
        key: 'priority',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const priorityKey = digObject(parentTask, 'priority', 'default')

        return <StatusBadge color={getColor('priority', priorityKey)} ghost text={priorityKey} />
      },
    },
    {
      title: 'Assignee',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 91,
      },
      dataKey: 'assignee_id',
      render: (assigneeId: number) => {
        const assignee = users[assigneeId] || {}
        // @ts-expect-error TS2538
        const image = images[assignee.image] || {}

        return (
          <UserProfileImage
            boxProps={{ display: 'inline-flex' }}
            hintComponent={(
              <UserProfileImage.Hint>
                {assignee.full_name || 'Unassigned'}
              </UserProfileImage.Hint>
            )}
            imageUrl={image.thumb_url}
          >
            <FontAwesomeIcon icon={assignee.id ? faUser : faUserSlash} />
          </UserProfileImage>
        )
      },
    },
  ]
}

interface TableProps {
  entities: EntitiesState,
  taskListItems: TaskListItemModel[],
}

const Table = (props: TableProps) => {
  const { entities, taskListItems } = props

  return (
    <BaseTable
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={buildColumns(entities)}
      data={taskListItems}
      stickyHeader
    />
  )
}

export default Table
